/**
 * terrace.ts — Cutting a plot into individual cottages.
 *
 * A plot in `BUILDINGS.LIST` is a *collision rect*, not a house. Building one
 * box per plot is what made the town read as eight office blocks: eight
 * identical extrusions, each as wide as a whole block, each with one door.
 *
 * A market town isn't built that way. It's built one narrow house at a time,
 * against the one next door, each a bit taller or shorter than its neighbour —
 * so the street elevation is a row of gable ends stepping up and down rather
 * than a single wall with windows punched in it. This module does that cut:
 * given a plot and which way it fronts, it hands back the individual cottages
 * that fill it.
 *
 * The collision rect is untouched. The bays tile the plot exactly, so the town
 * is solid in precisely the same places it was before — this is a change to what
 * gets drawn, not to where you can walk.
 *
 * `layOutTown()` at the bottom is the whole thing applied to the real config:
 * hand it nothing and it tells you every cottage in the town. It lives here
 * rather than in `render/buildings.ts` on purpose — deciding where the houses go
 * is arithmetic, and belongs somewhere it can be tested in Node, leaving the
 * renderer to do nothing but turn the answers into meshes.
 */
import { BUILDINGS } from '../config/constants';
import { activeFootways, activeLevel } from '../config/level';
import { hash2 } from '../utils/math';
import { frontFacing, rectCenter, type Facing, type Rect } from './level';

/** One cottage in a terrace: its own footprint, and where it sits in the row. */
export interface Bay extends Rect {
  /** 0-based position along the frontage. */
  index: number;
  /** How many cottages share this plot. */
  count: number;
}

/**
 * How many cottages fit across a frontage.
 *
 * **Floored**, not rounded: `minWidth` is a hard minimum, because a cottage
 * narrower than that can't fit a door with its architrave and still have wall
 * either side of it. Rounding would hand a 130-wide plot three 43-wide houses
 * and the doors would meet in the middle.
 */
export function bayCount(frontage: number, minWidth: number, maxBays: number): number {
  if (!(frontage > 0) || !(minWidth > 0)) return 1;
  return Math.max(1, Math.min(maxBays, Math.floor(frontage / minWidth)));
}

/**
 * The shortest a bay may be **along the frontage**, given how deep the plot is
 * and which way it faces.
 *
 * One rule — every cottage at least `BAY_MIN_RATIO` times deeper than it is
 * wide — but it lands on opposite sides of the cut depending on the frontage,
 * and this is the asymmetry the whole layout turns on.
 *
 * On a **north or south** frontage the cut runs across x, so a bay is
 * `frontage / count` wide and the full plot `depth` back. Depth is fixed, so
 * the rule caps how *wide* a bay may be — and `bayCount` only ever divides the
 * frontage further, which makes bays narrower and can't break it. Nothing to do
 * here but the door minimum; the cap is kept where the plot's frontage is
 * chosen (`frontageFor` in `levelgen/town.ts`).
 *
 * On an **east or west** frontage the cut runs the other way: a bay is `depth`
 * wide and `frontage / count` back. Now the rule is a *minimum* on the length
 * of each bay, and it has to be enforced right here, at the cut — divide such a
 * frontage on the door minimum alone and every bay comes out wider than it is
 * deep, which swings the whole terrace's ridge through a right angle and turns
 * a roof slope to the camera.
 *
 * Because `bayCount` floors, a count taken against this minimum can only ever
 * give bays *longer* than it — so the guarantee survives the split however the
 * frontage divides.
 */
export function bayMinLength(
  depth: number,
  alongX: boolean,
  minWidth: number,
  ratio: number,
): number {
  return alongX ? minWidth : Math.max(minWidth, depth * ratio);
}

/**
 * Cut a plot into cottages across its frontage.
 *
 * `alongX` says the frontage runs along x — i.e. the plot fronts north or south
 * — so the cut is made along x and each cottage runs the full depth back. That
 * orientation is the whole point: a cottage deeper than it is wide puts its
 * **ridge front-to-back**, which turns its gable end to the street. It's the
 * signature of the look, and it falls out of the proportions — but only if the
 * proportions are actually held, which is what `bayMinLength` above is for.
 *
 * Widths are jittered a little around the mean, and always sum to exactly the
 * frontage — a terrace of identical widths reads as a repeat, and a terrace
 * that doesn't tile leaves gaps you can see the ground through. `jitter` is a
 * *ceiling*: on a plot that only just divides, there's no room to vary the
 * widths without pushing one under `minWidth`, and the minimum wins.
 */
export function splitFrontage(
  plot: Rect,
  alongX: boolean,
  minWidth: number,
  maxBays: number,
  jitter = 0,
): Bay[] {
  const frontage = alongX ? plot.w : plot.h;
  const count = bayCount(frontage, minWidth, maxBays);
  const mean = frontage / count;

  // Jitter can't be allowed to undo the minimum. A plot that divides into three
  // 47-wide cottages has almost no room to vary them before one drops under 46
  // and loses its door — so the amount asked for is a ceiling, and what's
  // actually available is whatever the mean has spare over the minimum.
  const room = Math.max(0, Math.min(jitter, 1 - minWidth / mean));

  // Deterministic ±jitter fractions that sum to zero, so the widths still tile.
  const wobble: number[] = [];
  for (let i = 0; i < count; i++) {
    wobble.push((hash2(plot.x + i * 13, plot.y + i * 29) - 0.5) * 2 * room);
  }
  const mid = wobble.reduce((a, b) => a + b, 0) / count;

  const bays: Bay[] = [];
  let cut = 0;
  for (let i = 0; i < count; i++) {
    // The last bay takes whatever is left, so rounding can't open a seam.
    const width = i === count - 1 ? frontage - cut : mean * (1 + wobble[i] - mid);
    bays.push({
      x: alongX ? plot.x + cut : plot.x,
      y: alongX ? plot.y : plot.y + cut,
      w: alongX ? width : plot.w,
      h: alongX ? plot.h : width,
      index: i,
      count,
    });
    cut += width;
  }
  return bays;
}

/**
 * Wall heights down a terrace, stepping so no two neighbours match.
 *
 * The step is what the eye actually reads at this distance: two cottages of the
 * same height with different paint still look like one building with a stripe
 * down it, because their eaves and ridge lines run straight through. Forcing a
 * change at every party wall is what breaks a terrace into houses.
 */
export function bayHeights(base: number, count: number, step: number, seed: number): number[] {
  const levels: number[] = [];
  for (let i = 0; i < count; i++) {
    // −1, 0 or +1 storeys' worth of step off the plot's own height.
    let level = Math.min(2, Math.floor(hash2(seed + i * 7.3, seed * 1.7 + i) * 3)) - 1;
    // Nudge rather than re-roll when it matches the neighbour: re-rolling from
    // the same seed gives the same answer, and this way the run of levels can
    // still climb or fall rather than always alternating.
    if (i > 0 && level === levels[i - 1]) level = level === 1 ? -1 : level + 1;
    levels.push(level);
  }
  return levels.map((level) => base + level * step);
}

/**
 * Where the front door and its ground-floor windows sit along a cottage's
 * street face.
 *
 * A cottage is narrow, and its ground floor has to fit a door, its architrave,
 * and as much glazing as will go beside it. Three outcomes, widest first:
 *
 * - **room for both** — door centred, a window each side.
 * - **room for one** — the door slides off to one side and a single window takes
 *   the other. The classic narrow terrace front, and the case that matters: with
 *   the door left centred neither window fits, and the whole painted ground
 *   floor comes out blank either side of it.
 * - **room for neither** — just the door.
 *
 * Widths are the **full** width of each element, `gap` the clear wall between
 * two of them, and `margin` the clear wall left at each end of the face. Note
 * this margin is much smaller than `FACE_MARGIN`, which sizes the upper-storey
 * grid: that one keeps windows off the corner of a 126-wide block, and applied
 * to a 52-wide cottage it eats the whole ground floor.
 *
 * `index` alternates which side the door goes down a terrace, so neighbouring
 * doors sit apart rather than meeting either side of a party wall.
 */
export function frontPlan(
  faceW: number,
  doorW: number,
  winW: number,
  gap: number,
  margin: number,
  index = 0,
): { door: number; windows: number[] } {
  const usable = faceW - margin * 2;

  // Window, door, window — centred.
  if (winW * 2 + doorW + gap * 2 <= usable) {
    const at = doorW / 2 + gap + winW / 2;
    return { door: 0, windows: [-at, at] };
  }

  // Door and one window, as a pair centred on the face.
  if (doorW + winW + gap <= usable) {
    const pair = doorW + gap + winW;
    const side = index % 2 === 0 ? -1 : 1;
    // Left edge of the pair, then step in by half of whichever comes first.
    const door = side * (pair / 2 - doorW / 2);
    const window = -side * (pair / 2 - winW / 2);
    return { door, windows: [window] };
  }

  return { door: 0, windows: [] };
}

/**
 * One cottage, as everything downstream of the terrace cut sees it: its own
 * footprint and height, and the tone it's painted in.
 *
 * Structurally what a plot in `BUILDINGS.LIST` used to be, which is why the
 * home zone's one-off (`buildCottage`) can still be built from the same parts
 * without being in the town's list at all.
 */
export interface HouseSpec {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Height of the walls, before the eaves band and the roof on top of them. */
  tall: number;
  color: number;
  roof: string;
  roofColor: number;
  paint: number;
  material: string;
}

/**
 * A cottage placed in the world: its spec, its centre, and the two things it
 * can only know from the terrace it belongs to.
 *
 * `front` is the **plot's** front, not the bay's. A single cottage in a row is
 * narrow and deep, so asking `frontFacing()` about it on its own would often
 * pick a side wall — and half a terrace would end up with its doors round the
 * corner from the other half.
 *
 * `over` is the roof overhang on each side, and it's zero at a **party wall**.
 * Neighbours share that wall, so two full overhangs there would drive each
 * roof ten units into the one next door, and with the stepped heights the
 * taller one's eaves would come out through its neighbour's tiles.
 */
export interface Cottage {
  spec: HouseSpec;
  front: Facing;
  cx: number;
  cz: number;
  over: { x0: number; x1: number; z0: number; z1: number };
  /** Where the door and its ground-floor windows sit along the street face. */
  plan: { door: number; windows: number[] };
}

/**
 * Work out every cottage in the town: which plot it's on, how wide, how tall,
 * and what it's painted.
 *
 * Deterministic from the level alone — same town on every run — so this is
 * also the thing to call from a test that needs to know where the houses
 * actually are, as opposed to where the collision rects are.
 */
export function layOutTown(): Cottage[] {
  const { BAY_MAX: maxBays, BAY_JITTER: jitter, BAY_STEP: step } = BUILDINGS;
  const over = BUILDINGS.ROOF_OVERHANG;
  const out: Cottage[] = [];

  const level = activeLevel();
  const footways = activeFootways();
  for (const plot of level.buildings) {
    const front = frontFacing(plot, footways);
    // A plot fronting north or south has its frontage along x, so that's the
    // way the terrace is cut and the way the party walls run.
    const alongX = front === 'north' || front === 'south';
    const minW = bayMinLength(
      alongX ? plot.h : plot.w,
      alongX,
      BUILDINGS.BAY_MIN_WIDTH,
      BUILDINGS.BAY_MIN_RATIO,
    );
    const bays = splitFrontage(plot, alongX, minW, maxBays, jitter);
    const heights = bayHeights(plot.tall, bays.length, step, plot.x + plot.y);

    bays.forEach((bay, i) => {
      const tone = plot.tones[i % plot.tones.length];
      const [cx, cz] = rectCenter(bay);
      // No overhang where this cottage meets the next one along the row.
      const first = i === 0;
      const last = i === bays.length - 1;
      out.push({
        spec: {
          x: bay.x,
          y: bay.y,
          w: bay.w,
          h: bay.h,
          tall: heights[i],
          roof: 'gable',
          color: tone.color,
          roofColor: tone.roofColor,
          paint: tone.paint,
          material: tone.material,
        },
        front,
        cx,
        cz,
        over: {
          x0: alongX && !first ? 0 : over,
          x1: alongX && !last ? 0 : over,
          z0: !alongX && !first ? 0 : over,
          z1: !alongX && !last ? 0 : over,
        },
        plan: frontPlan(
          alongX ? bay.w : bay.h,
          BUILDINGS.DOOR_WIDTH + 6, // the architrave, not just the door
          BUILDINGS.WINDOW_W + BUILDINGS.WINDOW_SURROUND_PAD * 2,
          BUILDINGS.FRONT_GAP,
          BUILDINGS.FRONT_MARGIN,
          i,
        ),
      });
    });
  }
  return out;
}
